import { Button, Chip, Divider, Stack, styled, Typography } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { DynamicIcon } from 'src/components';
import { Conversation, getStatusDisplay } from 'src/graphql';
import { EntityNames } from 'src/graphql/types';
import { TabType, useCustomView, useGlobalTabs } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { Platform } from 'src/utils/system';

const StyledButton = styled(Button)(() => ({
  color: 'inherit',
  display: 'flex',
  flex: '1',
  overflow: 'hidden',
  paddingInlineEnd: '0.25rem',
  textDecoration: 'none',
  textTransform: 'unset',
  width: '100%',
  padding: 0,
}));

const getConversationDisplayData = (conversation: Conversation) => {
  const statusDisplayProps = getStatusDisplay(conversation.conversationStatusId);
  const summaryText = conversation.latestSummaryText?.trim();
  const assigneeGroupName = conversation.conversationAssigneeGroup?.name?.trim();
  const assigneeName = conversation.assigneeFullName?.trim();
  let assigneeLabel: string | null = null;

  if (assigneeGroupName) {
    assigneeLabel = assigneeName
      ? `${assigneeGroupName} / ${assigneeName}`
      : assigneeGroupName;
  }

  return { statusDisplayProps, summaryText, assigneeLabel };
};

export const ConversationTooltipContent = ({
  conversation,
}: {
  conversation: Conversation;
}) => {
  const { statusDisplayProps, summaryText, assigneeLabel } =
    getConversationDisplayData(conversation);

  return (
    <Stack direction="column" spacing={1} p={1}>
      <Typography variant="subtitle2" fontWeight={600}>
        {conversation.subject}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <DynamicIcon
          name={statusDisplayProps.icon}
          library={statusDisplayProps.library}
          sx={{
            color: statusDisplayProps.color,
            width: '1.125rem',
            height: '1.125rem',
          }}
          aria-label={conversation.conversationStatusId}
        />
        <Typography variant="body2" color="text.secondary">
          {conversation.friendlyId}
        </Typography>
      </Stack>
      {assigneeLabel ? (
        <Chip
          size="small"
          label={assigneeLabel}
          icon={<DynamicIcon name="users" library="lucide" />}
          sx={{
            alignSelf: 'flex-start',
            fontSize: '0.75rem',
            height: '1.5rem',
            '& .MuiChip-icon': {
              color: 'text.secondary',
            },
          }}
        />
      ) : null}
      {!!summaryText && (
        <>
          <Divider />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
            }}
          >
            {summaryText}
          </Typography>
        </>
      )}
    </Stack>
  );
};

export const ConversationInfo = ({
  conversation,
  onTooltipOpen,
  onTooltipClose,
}: {
  conversation: Conversation;
  onTooltipOpen?: (conversation: Conversation, anchorEl: HTMLElement) => void;
  onTooltipClose?: () => void;
}) => {
  const { navigate } = useNavigateRoute();
  const { openInTab } = useGlobalTabs();
  const customViewId = useCustomView(EntityNames.Conversation)?.customViewId;
  if (!conversation) {
    return;
  }
  const { statusDisplayProps } = getConversationDisplayData(conversation);

  return (
    <StyledButton
      variant="text"
      onMouseEnter={(event) => onTooltipOpen?.(conversation, event.currentTarget)}
      onMouseLeave={() => onTooltipClose?.()}
      onFocus={(event) => onTooltipOpen?.(conversation, event.currentTarget)}
      onBlur={() => onTooltipClose?.()}
      onClick={(e) => {
        if (Platform.isMacOS ? e.metaKey : e.ctrlKey) {
          openInTab(
            { type: TabType.Conversation, entity: conversation },
            { customViewId },
            false,
          );
          return;
        }
        navigate(portalPaths.conversation.full(conversation.friendlyId), {
          state: { customViewId },
        });
      }}
    >
      <Stack spacing={2} direction="row" alignItems="center" overflow="hidden" width="100%">
        <DynamicIcon
          name={statusDisplayProps.icon}
          library={statusDisplayProps.library}
          sx={{
            color: statusDisplayProps.color,
            width: '1.5rem',
            height: '1.5rem',
          }}
          aria-label={conversation.conversationStatusId}
        />
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {conversation.subject}
        </Typography>
        <Typography
          fontSize="0.6875rem"
          fontWeight={500}
          textAlign="end"
          flex="1"
          whiteSpace="nowrap"
        >
          {Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }).format(new Date(conversation.lastMessageReceivedAt))}
        </Typography>
      </Stack>
    </StyledButton>
  );
};
